const SESSION_STORAGE_KEY = "equinox-lead-tracking-v1";

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL;
const getApiBase = () => {
  const backendUrl = getBackendUrl();
  return backendUrl ? `${backendUrl}/api` : "";
};

const buildSessionId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `session-${Date.now()}`;
};

const sanitizeText = (value) => (typeof value === "string" ? value.trim() : "");

export const getSavedTracking = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const captureLeadTracking = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const url = new URL(window.location.href);
    const query = url.searchParams;
    const existing = getSavedTracking() || {};
    const sessionId = existing.session_id || buildSessionId();
    const tracking = {
      session_id: sessionId,
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || existing.referrer || "",
      attribution: {
        utm_source: sanitizeText(query.get("utm_source") || existing.attribution?.utm_source),
        utm_medium: sanitizeText(query.get("utm_medium") || existing.attribution?.utm_medium),
        utm_campaign: sanitizeText(query.get("utm_campaign") || existing.attribution?.utm_campaign),
        utm_content: sanitizeText(query.get("utm_content") || existing.attribution?.utm_content),
        utm_term: sanitizeText(query.get("utm_term") || existing.attribution?.utm_term),
        gclid: sanitizeText(query.get("gclid") || existing.attribution?.gclid),
        fbclid: sanitizeText(query.get("fbclid") || existing.attribution?.fbclid),
        msclkid: sanitizeText(query.get("msclkid") || existing.attribution?.msclkid),
        first_page: sanitizeText(existing.attribution?.first_page || window.location.pathname),
        landing_url: sanitizeText(existing.attribution?.landing_url || window.location.href),
        page_path: window.location.pathname,
        referrer: document.referrer || existing.attribution?.referrer || "",
        session_id: sessionId,
        landing_timestamp: existing.attribution?.landing_timestamp || new Date().toISOString(),
      },
    };

    try {
      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(tracking));
    } catch (storageError) {
      console.warn("Lead tracking storage unavailable", storageError);
    }

    return tracking;
  } catch (error) {
    console.warn("Lead tracking capture unavailable", error);
    return null;
  }
};

const sendTrackingEvent = async (payload) => {
  const apiBase = getApiBase();
  if (!apiBase) {
    return;
  }

  try {
    await fetch(`${apiBase}/tracking/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (error) {
    console.warn("Tracking event failed", error);
  }
};

export const trackLeadEvent = (eventName, data = {}) => {
  const tracking = captureLeadTracking();
  if (!tracking) {
    return;
  }

  void sendTrackingEvent({
    event_name: eventName,
    label: data.label || "",
    page_path: tracking.page_path,
    page_url: tracking.page_url,
    referrer: tracking.referrer,
    session_id: tracking.session_id,
    metadata: data.metadata || {},
    attribution: tracking.attribution,
    source: data.source || "website",
  });
};

export const getLeadRequestPayload = (form, extras = {}) => {
  const tracking = captureLeadTracking();
  return {
    ...form,
    source: extras.source || "contact_page",
    page_url: tracking?.page_url || "",
    page_path: tracking?.page_path || "",
    referrer: tracking?.referrer || "",
    session_id: tracking?.session_id || "",
    whatsapp_handoff_opened: Boolean(extras.whatsapp_handoff_opened),
    attribution: tracking?.attribution || undefined,
  };
};

export const initializeAdTracking = () => {
  const tracking = captureLeadTracking();
  const metaPixelId = process.env.REACT_APP_META_PIXEL_ID;
  const ga4MeasurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  const googleAdsConversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;

  if (typeof window === "undefined") {
    return;
  }

  if (!window.__equinox_gtag_initialized && (ga4MeasurementId || googleAdsConversionId)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId || googleAdsConversionId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    window.gtag("js", new Date());

    if (ga4MeasurementId) {
      window.gtag("config", ga4MeasurementId, { send_page_view: false });
    }
    if (googleAdsConversionId) {
      window.gtag("config", googleAdsConversionId);
    }

    window.__equinox_gtag_initialized = true;
  }

  if (!window.__equinox_meta_initialized && metaPixelId) {
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = function () {
        if (n.callMethod) {
          n.callMethod.apply(n, arguments);
        } else {
          n.queue.push(arguments);
        }
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

    window.fbq("init", metaPixelId);
    window.__equinox_meta_initialized = true;
  }

  if (tracking) {
    const pagePath = tracking.page_path;
    if (window.gtag && ga4MeasurementId) {
      window.gtag("event", "page_view", {
        page_path: pagePath,
        page_location: tracking.page_url,
        page_title: document.title,
      });
    }

    if (window.fbq && metaPixelId) {
      window.fbq("track", "PageView");
    }
  }
};

export const trackAdClickEvent = ({ label, metadata = {}, eventName = "cta_click" }) => {
  const metaPixelId = process.env.REACT_APP_META_PIXEL_ID;
  const ga4MeasurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;

  trackLeadEvent(eventName, { label, metadata });

  if (typeof window === "undefined") {
    return;
  }

  if (window.gtag && ga4MeasurementId) {
    window.gtag("event", eventName, { label, ...metadata });
  }

  if (window.fbq && metaPixelId) {
    window.fbq("trackCustom", eventName.toUpperCase(), { label, ...metadata });
  }
};

export const trackLeadSubmitSuccess = ({ leadId, source = "contact_form" }) => {
  const metaPixelId = process.env.REACT_APP_META_PIXEL_ID;
  const ga4MeasurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  const googleAdsConversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
  const googleAdsConversionLabel = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;

  trackLeadEvent("lead_submit", {
    label: source,
    metadata: { lead_id: leadId, source },
  });

  if (typeof window === "undefined") {
    return;
  }

  if (window.gtag && ga4MeasurementId) {
    window.gtag("event", "generate_lead", { lead_id: leadId, source });
  }

  if (window.gtag && googleAdsConversionId && googleAdsConversionLabel) {
    window.gtag("event", "conversion", {
      send_to: `${googleAdsConversionId}/${googleAdsConversionLabel}`,
      value: 1,
      currency: "INR",
    });
  }

  if (window.fbq && metaPixelId) {
    window.fbq("track", "Lead", { lead_id: leadId, source });
  }
};
